import { useEffect, useState } from 'react';
import { onOfflineChange, pendingCount, replayOutbox } from '@/offline/sync';
import { pingThisDevice, purgeIfSilentTooLong } from '@/offline/device';

/**
 * Whether the app has a network (ToR 1.0(xii)), how much waits in the outbox, and the replay that runs
 * the moment the network returns. The device is also pinged on return, so a wipe asked for while it was
 * away is carried out at once.
 */
export function useOfflineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [lastReplay, setLastReplay] = useState<{ sent: number; failed: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => { pendingCount().then((n) => { if (!cancelled) setPending(n); }).catch(() => undefined); };
    const goOnline = async () => {
      setOnline(true);
      await pingThisDevice();
      const result = await replayOutbox();
      if (!cancelled) setLastReplay(result);
      refresh();
    };
    const goOffline = () => { setOnline(false); void purgeIfSilentTooLong(); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    const off = onOfflineChange(refresh);
    refresh();
    if (navigator.onLine) void replayOutbox().then((r) => { if (!cancelled) setLastReplay(r); refresh(); });
    return () => { cancelled = true; window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); off(); };
  }, []);

  return { online, pending, lastReplay };
}
